import { ArrowLeftIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { AxiosError } from 'axios';
import { type MutableRefObject,useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { Input } from '@/components/core/Input';
import { Loader } from '@/components/core/Loader';
import { Panel, PanelHeader } from '@/components/core/Panel';
import { DiscardChangesConfirmationModal } from '@/components/DiscardChangesConfirmationModal';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';

import { AddRolesPanel } from './components/AddRolesDialog';
import { AddUsersPanel } from './components/AddUsersDialog';
import {
    currentOrganizationId,
    goToUserGroups,
    readGroupId,
    rememberUserGroupToast,
} from './user-groups.location';
import {
    useCreateUserGroup,
    useOrganizationUsers,
    UserGroupErrorBody,
    useUpdateUserGroup,
    useUserGroups,
} from './user-groups.queries';
import {
    GroupMember,
    sameValues,
    sentenceCase,
    UserGroupBaseline,
} from './user-groups.types';

function errorPlaceholder(data: UserGroupErrorBody['data']): string {
    if (Array.isArray(data)) {
        const first = data[0];
        return first == null ? '' : String(first);
    }
    if (typeof data === 'string' || typeof data === 'number') {
        return String(data);
    }
    return '';
}

function saveErrorText(
    error: AxiosError<UserGroupErrorBody>,
    translate: (key: string, values?: Record<string, string | number>) => string,
) {
    const body = error.response?.data;
    const key = body?.key || 'SAVE_ERROR_MESSAGE';
    return translate(key, { value: errorPlaceholder(body?.data) });
}

interface AddUserGroupActionState {
    saveDisabled: boolean;
    saving: boolean;
}

interface AddUserGroupPageProps {
    embedded?: boolean;
    groupId?: string;
    onFinished?: () => void;
    onActionStateChange?: (state: AddUserGroupActionState) => void;
    actionsRef?: MutableRefObject<{ save: () => void; cancel: () => void } | null>;
}

export default function AddUserGroupPage({
    embedded = false,
    groupId: groupIdProp,
    onFinished,
    onActionStateChange,
    actionsRef,
}: AddUserGroupPageProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const groupId = embedded ? groupIdProp : readGroupId();

    const { data: groups, isLoading: groupsLoading } = useUserGroups();
    const { data: allUsers, isLoading: usersLoading } = useOrganizationUsers({
        enabled: Boolean(groupId),
    });

    const [groupName, setGroupName] = useState('');
    const [members, setMembers] = useState<GroupMember[]>([]);
    const [roles, setRoles] = useState<string[]>([]);
    const [baseline, setBaseline] = useState<UserGroupBaseline | null>(null);
    const [ready, setReady] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);

    const loadedGroup = useMemo(
        () => groups?.find((group) => String(group.id) === groupId),
        [groups, groupId],
    );
    const editMode = Boolean(loadedGroup);

    useEffect(() => {
        if (ready) {
            return;
        }
        if (groupId && (groupsLoading || usersLoading)) {
            return;
        }
        if (!loadedGroup) {
            setReady(true);
            return;
        }
        const groupUserIds = (loadedGroup.userIds ?? []).map(String);
        const nextMembers = (allUsers ?? []).filter((user) => groupUserIds.includes(String(user.id)));
        const nextRoles = [...(loadedGroup.roles ?? [])];
        const nextName = loadedGroup.groupName ?? '';
        setGroupName(nextName);
        setMembers(nextMembers);
        setRoles(nextRoles);
        setBaseline({
            groupName: nextName,
            userIds: nextMembers.map((user) => String(user.id)),
            roles: nextRoles,
        });
        setReady(true);
    }, [ready, groupId, groupsLoading, usersLoading, loadedGroup, allUsers]);

    const isDirty = editMode && baseline != null && (
        groupName !== baseline.groupName
        || !sameValues(members.map((member) => String(member.id)), baseline.userIds)
        || !sameValues(roles, baseline.roles)
    );

    const { mutate: createGroup, isPending: isCreating } = useCreateUserGroup({
        onSuccess: () => {
            if (onFinished) {
                toast.success('USER_GROUP.ADD_SUCCESS');
                onFinished();
                return;
            }
            rememberUserGroupToast('USER_GROUP.ADD_SUCCESS');
            goToUserGroups();
        },
        onError: (error) => {
            toast.error(saveErrorText(error, t));
        },
    });
    const { mutate: updateGroup, isPending: isUpdating } = useUpdateUserGroup({
        onSuccess: () => {
            if (onFinished) {
                toast.success('USER_GROUP.UPDATE_SUCCESS');
                onFinished();
                return;
            }
            rememberUserGroupToast('USER_GROUP.UPDATE_SUCCESS');
            goToUserGroups();
        },
        onError: (error) => {
            toast.error(saveErrorText(error, t));
        },
    });

    const isSaving = isCreating || isUpdating;
    const saveDisabled = !ready || groupName.trim() === '' || (editMode && !isDirty) || isSaving;

    const requestLeave = () => {
        if (editMode && isDirty) {
            setConfirmLeave(true);
            return;
        }
        if (onFinished) {
            onFinished();
            return;
        }
        goToUserGroups();
    };

    const saveGroup = () => {
        if (saveDisabled) {
            return;
        }
        const payload = {
            id: editMode ? loadedGroup?.id : undefined,
            groupName: groupName.trim(),
            organizationId: currentOrganizationId(),
            userIds: members.map((member) => String(member.id)),
            roles,
        };
        if (editMode) {
            updateGroup(payload);
        } else {
            createGroup(payload);
        }
    };

    const saveRef = useRef(saveGroup);
    const cancelRef = useRef(requestLeave);
    saveRef.current = saveGroup;
    cancelRef.current = requestLeave;
    if (actionsRef) {
        actionsRef.current = {
            save: () => saveRef.current(),
            cancel: () => cancelRef.current(),
        };
    }

    useEffect(() => {
        onActionStateChange?.({
            saveDisabled,
            saving: isCreating || isUpdating,
        });
    }, [onActionStateChange, saveDisabled, isCreating, isUpdating]);

    const removeUser = (userId: number) => {
        setMembers((current) => current.filter((member) => member.id !== userId));
    };

    const removeRole = (role: string) => {
        setRoles((current) => current.filter((item) => item !== role));
    };

    const formTitle = sentenceCase((editMode || (Boolean(groupId) && !ready))
        ? t('USER_GROUP.EDIT_GROUP')
        : t('USER_GROUP.ADD_GROUP'));

    const form = (
        <>
            {!embedded ? (
                <PanelHeader
                    icon={UserGroupIcon}
                    title={formTitle}
                />
            ) : null}
            <div className={embedded ? undefined : 'scoped-panel-header tw-h-auto tw-overflow-auto'}>
                {!ready ? (
                    <div className="tw-flex tw-justify-center tw-py-8">
                        <Loader />
                    </div>
                ) : (
                    <>
                        {!embedded && editMode ? (
                            <a
                                href="/user-groups"
                                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-t-sm tw-text-t-primary lg:tw-text-t-base"
                                onClick={(event) => {
                                    event.preventDefault();
                                    requestLeave();
                                }}
                            >
                                <ArrowLeftIcon className="tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6" aria-hidden="true" />
                                <span>{sentenceCase(t('USER_GROUP.USER_GROUPS'))}</span>
                            </a>
                        ) : null}
                        <div className={embedded || !editMode ? '' : 'tw-mt-4'}>
                            <div className="tw-flex tw-flex-col tw-gap-2 md:tw-flex-row md:tw-items-center md:tw-gap-3">
                                <div className="tw-flex tw-min-w-0 tw-items-center md:tw-w-2/5 md:tw-shrink-0 md:tw-justify-end md:tw-text-right">
                                    <label
                                        htmlFor="group_name_input"
                                        className="tw-m-0 tw-block tw-min-w-0 tw-text-t-sm tw-text-t-secondary lg:tw-text-t-base"
                                    >
                                        {sentenceCase(t('USER_GROUP.GROUP_NAME'))}
                                    </label>
                                </div>
                                <div className="tw-min-w-0 tw-w-full md:tw-flex-1">
                                    <Input
                                        id="group_name_input"
                                        name="group_name_input"
                                        className="tw-w-full"
                                        value={groupName}
                                        autoFocus
                                        required
                                        onChange={(event) => setGroupName(event.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <section className="tw-mt-6">
                            <h2 className="tw-mb-2 tw-font-normal tw-text-t-sm lg:tw-text-t-base">
                                {t('USER_GROUP.ADD_USERS')}
                            </h2>
                            <AddUsersPanel
                                open
                                members={members}
                                onAdd={(added) => {
                                    setMembers((current) => {
                                        const ids = new Set(current.map((member) => String(member.id)));
                                        const next = added.filter((user) => !ids.has(String(user.id)));
                                        return [...current, ...next];
                                    });
                                }}
                                onRemove={(user) => removeUser(user.id)}
                            />
                        </section>

                        <section className="tw-mt-6">
                            <h2 className="tw-mb-2 tw-font-normal tw-text-t-sm lg:tw-text-t-base">
                                {t('USER_GROUP.ADD_ROLES')}
                            </h2>
                            <AddRolesPanel
                                open
                                roles={roles}
                                onAdd={(added) => {
                                    setRoles((current) => {
                                        const next = added.filter((role) => !current.includes(role));
                                        return [...current, ...next];
                                    });
                                }}
                                onRemove={removeRole}
                            />
                        </section>

                        {!embedded ? (
                            <div className="tw-mt-4 tw-flex tw-items-center tw-justify-end tw-gap-2">
                                <Button
                                    id="groupSaveButton"
                                    disabled={saveDisabled}
                                    loading={isCreating || isUpdating}
                                    onClick={saveGroup}
                                >
                                    {sentenceCase(t('USER_GROUP.SAVE_GROUP'))}
                                </Button>
                                <Button className="!tw-h-auto" variant="secondary" onClick={requestLeave}>
                                    {t('CANCEL')}
                                </Button>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
            <DiscardChangesConfirmationModal
                show={confirmLeave}
                onDiscard={() => {
                    setConfirmLeave(false);
                    if (onFinished) {
                        onFinished();
                        return;
                    }
                    goToUserGroups();
                }}
                onClose={() => setConfirmLeave(false)}
            />
        </>
    );

    if (embedded) {
        return form;
    }

    return <Panel>{form}</Panel>;
}
